import config from "../config";
import { Cart, ComboItems, ItemPricing, SizeRecommendation } from "../types";
import { UserInfo } from "../types/index";

export const canContinueToPayment = (
  isSaving: boolean,
  comboItems: ComboItems[],
  pricingCartData: Cart,
  userInfos: UserInfo
) => {
  const reducedItems: ItemPricing[] = getReducedCartItems(comboItems);
  return (
    !isSaving &&
    (pricingCartData.comboItems.every(
      (comboItem) =>
        comboItem.aboveMinimumQuantity &&
        comboItem.items.every((item) => item.minimumQuantityPerSizeReached) &&
        !comboItem.items.some((item) => item.productVersionNeedUpgrade)
    ) ||
      ((userInfos.isAdmin || userInfos.isDesigner) &&
        reducedItems.every((item) => item.quantity > 0)))
  );
};

export const getReducedCartItems = (comboItems: ComboItems[]) => {
  return comboItems?.reduce(
    (acc, combo) => [...acc, ...combo.items],
    [] as ItemPricing[]
  );
};

export const cartContainsDeprecatedDesign = (comboItems: ComboItems[]) => {
  return (
    comboItems.some((comboItem) =>
      comboItem.items.some((i) => i?.deprecatedDesign)
    ) && config.blockPaymentWhenDesignIsDeprecated
  );
};

export const cartContainsItemsThatExceedMax = (comboItems: ComboItems[]) => {
  return comboItems.some((comboItem) => !comboItem.belowMaximumQuantity);
};

export const isQuoteRequest = (
  pricing: Cart | undefined,
  techpack: { projectNumber?: string } | undefined
): boolean => {
  return (
    (pricing?.quoteRequest || !pricing?.allItemsAreBelowMaximumQuantity) &&
    !techpack?.projectNumber
  );
};

export const getSizeQtyFromRecommendations = (
  sizeRecommendations: SizeRecommendation[],
  totalQuantity: number,
  options: {
    minPerSize?: number | number[];
    activationFraction?: number;
  } = {}
): Array<{ size: string; quantity: number }> => {
  if (!sizeRecommendations?.length || totalQuantity <= 0) {
    return [];
  }

  const sizePercentages = sizeRecommendations.map((rec) => rec.percentage);

  // Normalize percentages to ensure they sum to 1.0
  const percentageSum = sizePercentages.reduce((sum, p) => sum + p, 0);
  const normalizedPercentages = sizePercentages.map((p) => p / percentageSum);

  // Use the distributeSizes logic
  const quantities = distributeSizes(
    totalQuantity,
    normalizedPercentages,
    options
  );

  return sizeRecommendations.map((rec, i) => ({
    size: rec.size,
    quantity: quantities[i],
  }));

  function distributeSizes(
    total: number,
    sizePercentages: number[],
    options: {
      minPerSize?: number | number[];
      activationFraction?: number;
    } = {}
  ): number[] {
    const n = sizePercentages.length;
    const idxAll = [...Array(n).keys()];
    const sumW = sizePercentages.reduce((a, b) => a + b, 0) || 1;
    const w = sizePercentages.map((p) => p / sumW); // normalize so shares are comparable to thresholds

    // --- Helper: D'Hondt (Jefferson) apportionment with optional seed counts and eligible set ---
    function dhondtAllocate(
      seats: number,
      weights: number[],
      seedCounts: number[] = [],
      eligible: number[] = idxAll
    ): number[] {
      const counts = seedCounts.length ? seedCounts.slice() : Array(n).fill(0);
      if (seats <= 0 || eligible.length === 0) return counts;

      for (let s = 0; s < seats; s++) {
        let best = eligible[0];
        let bestScore = (weights[best] || 0) / (counts[best] + 1);
        for (let j = 1; j < eligible.length; j++) {
          const i = eligible[j];
          const score = (weights[i] || 0) / (counts[i] + 1);
          if (
            score > bestScore ||
            (score === bestScore && (weights[i] || 0) > (weights[best] || 0))
          ) {
            best = i;
            bestScore = score;
          }
        }
        counts[best] += 1;
      }
      return counts;
    }

    // --- No per-size minimums: monotone allocation across all sizes ---
    if (!options.minPerSize) {
      return dhondtAllocate(total, w, Array(n).fill(0), idxAll);
    }

    // --- With per-size minimums + activation thresholds ---
    const minArr = Array.isArray(options.minPerSize)
      ? options.minPerSize
      : Array(n).fill(options.minPerSize);
    const actFrac = options.activationFraction ?? 0.75; // default 75% of min
    const thr = minArr.map((m: number) => actFrac * m);

    // Activate sizes in descending base percentage if share ≥ threshold and we can afford their min
    const order = idxAll.sort((a, b) => w[b] - w[a]);
    const active: number[] = [];
    let budget = total;
    for (const i of order) {
      const share = w[i] * total; // expected units from this size
      if (share >= thr[i] && budget >= minArr[i]) {
        active.push(i);
        budget -= minArr[i]; // reserve ability to pay their mins
      }
    }

    // Fallback: if nothing activates, put everything in the top-% size
    if (active.length === 0) {
      const out = Array(n).fill(0);
      out[order[0]] = total;
      return out;
    }

    // Targets over the WHOLE total, normalized among ACTIVE sizes
    const sumWActive = active.reduce((s, i) => s + w[i], 0) || 1;
    const target = Array(n).fill(0);
    for (const i of active) target[i] = total * (w[i] / sumWActive);

    // Lock any active size whose target < min to its minimum
    const counts = Array(n).fill(0);
    let lockedSum = 0;
    const unlocked: number[] = [];
    for (const i of active) {
      if (target[i] < minArr[i]) {
        counts[i] = minArr[i];
        lockedSum += counts[i];
      } else {
        unlocked.push(i);
      }
    }

    // Allocate remaining units using D'Hondt
    const remaining = total - lockedSum;
    const eligible = unlocked.length > 0 ? unlocked : active; // if none unlocked, spread across all active
    return dhondtAllocate(remaining, w, counts, eligible);
  }
};
